import { Box, Typography, IconButton } from "@mui/material";
import { CalendarClock, EllipsisVertical, Calendar } from "lucide-react";
import { TaskBlock } from "protogen/tasks_pb";
import { Task } from "protogen/tasks_pb";
import { format } from "date-fns";
import { useState, useRef } from "react";
import DateDisplay from "../../common/DateDisplay";
import useIsMobile from "components/hooks/useIsMobile";
import { BlockGutterNoBorder } from "./BlockGutter";
import PopperMenu, { createMenuOption } from "../../common/PopperMenu";
import TaskReminderDialog from "../TaskReminderDialog";
import EditEventDialog from "../../calendar/EditEventDialog";
import {
  useUpdateCalendarEvent,
  useDeleteCalendarEvent,
} from "services/calendar";
import {
  UpdateCalendarEventRequest,
  DeleteCalendarEventRequest,
} from "protogen/calendar_service_pb";
import { CalendarEvent, EventReminder } from "protogen/calendar_pb";

interface TaskEventBlockProps {
  block: TaskBlock;
  task: Task;
  accountType: "advisor" | "member";
  onUpdate?: () => void;
  event?: CalendarEvent | null;
}

const TaskEventBlock = ({
  block,
  task,
  accountType: _accountType,
  onUpdate,
  event = null,
}: TaskEventBlockProps) => {
  const isMobile = useIsMobile();
  const metadata = block.metadata ? JSON.parse(block.metadata) : {};
  const isDueDateEvent = metadata.taskEventType === "dueDate";
  const isExternal = metadata.isExternal || false;
  // The block.ref is the calendar event ref (since block.id = event.id)
  const eventRef = block.ref;

  const [menuOpen, setMenuOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFullEventDialogOpen, setEditFullEventDialogOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const { request: updateEvent } = useUpdateCalendarEvent(() => {
    onUpdate?.();
  });

  const { request: deleteEvent } = useDeleteCalendarEvent(() => {
    onUpdate?.();
  });

  // Read from the calendar event or metadata for date/time information
  const title = isExternal
    ? metadata.title || "External Event"
    : event?.title || "Task Reminder";
  const allDay = isExternal
    ? metadata.all_day || false
    : event?.allDay || false;

  let eventDate: Date | null = null;
  if (isExternal && metadata.event_datetime) {
    try {
      eventDate = new Date(metadata.event_datetime);
    } catch (e) {
      console.error("Error parsing external event date:", e);
    }
  } else if (event?.startSec) {
    try {
      eventDate = new Date(Number(event.startSec) * 1000);
    } catch (e) {
      console.error("Error parsing event date:", e);
    }
  }

  const createdDate = new Date(Number(block.createdSec) * 1000);

  const handleEdit = () => {
    setMenuOpen(false);
    if (event?.sourceEntityType === "task_event") {
      setEditFullEventDialogOpen(true);
    } else {
      setEditDialogOpen(true);
    }
  };

  const handleDelete = async () => {
    setMenuOpen(false);
    await deleteEvent(
      new DeleteCalendarEventRequest({
        eventRef: eventRef,
      }),
    );
  };

  const handleSave = async (newTitle: string, newEventDateTime: Date) => {
    const timestampSec = BigInt(Math.floor(newEventDateTime.getTime() / 1000));

    await updateEvent(
      new UpdateCalendarEventRequest({
        eventRef: eventRef,
        updatedTitle: newTitle,
        updatedStartSecUTC: timestampSec,
        updatedEndSecUTC: timestampSec,
        updatedAllDay: false,
        updatedReminders: [new EventReminder({ value: 0, unit: "minutes" })],
        updatedDisplayTimezone:
          Intl.DateTimeFormat().resolvedOptions().timeZone,
      }),
    );
  };

  // Format the full date and time string
  let dateTimeString = "";
  if (eventDate) {
    if (allDay) {
      dateTimeString = format(eventDate, "MMM d");
    } else {
      dateTimeString = format(eventDate, "MMM d 'at' h:mm a");
    }
  }

  const isTaskEvent = event?.sourceEntityType === "task_event";

  return (
    <>
      <Box sx={{ display: "flex", gap: isMobile ? "12px" : "16px" }}>
        <BlockGutterNoBorder
          icon={
            isTaskEvent ? (
              <Calendar size={20} color="#198282" />
            ) : (
              <CalendarClock size={20} color="#198282" />
            )
          }
          isLast={false}
        />
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexWrap: "wrap",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "4px",
            marginBottom: "8px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              flexWrap: "wrap",
              cursor: isExternal ? "default" : "pointer",
            }}
            onClick={isExternal ? undefined : handleEdit}
          >
            <Typography variant="body">{title}</Typography>
            {dateTimeString && (
              <Typography variant="body" color="text.tertiary">
                {dateTimeString}
              </Typography>
            )}
            {isExternal && metadata.externalCalendarName && (
              <Typography variant="bodySmall" color="text.tertiary">
                ({metadata.externalCalendarName})
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <DateDisplay date={createdDate} />
            {!isExternal && (
              <IconButton
                ref={anchorRef}
                onClick={() => setMenuOpen(true)}
                size="small"
              >
                <EllipsisVertical size={20} color="#8E9598" />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>

      <PopperMenu
        open={menuOpen}
        anchorRef={anchorRef}
        handleClose={() => setMenuOpen(false)}
      >
        {[
          createMenuOption("Edit", handleEdit, "pencil"),
          createMenuOption("Delete", handleDelete, "trash"),
        ]}
      </PopperMenu>

      <TaskReminderDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        mode="edit"
        event={event}
        hasDueDateEvent={!!task.dueDate && !isDueDateEvent}
        onSave={handleSave}
      />

      {event && (
        <EditEventDialog
          eventRef={eventRef}
          open={editFullEventDialogOpen}
          onClose={() => setEditFullEventDialogOpen(false)}
          onEdited={() => {
            setEditFullEventDialogOpen(false);
            onUpdate?.();
          }}
          accountType="advisor"
        />
      )}
    </>
  );
};

export default TaskEventBlock;
